import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";
import {
    EmojiPicker as FrimousseEmojiPicker,
    type EmojiPickerListComponents,
    type Locale,
    useActiveEmoji,
} from "frimousse";
import {useTranslation} from "react-i18next";

export interface EmojiSelection {
    emoji: string;
    label: string;
}

interface EmojiPickerProps {
    onSelect: (selection: EmojiSelection) => void;
    autoFocus?: boolean;
}

const listComponents: EmojiPickerListComponents = {
    CategoryHeader: ({category, ref: _ref, ...props}) => (
        <Box
            {...props}
            sx={{
                zIndex: 1,
                px: 1.5,
                pt: 1.25,
                pb: 0.75,
                bgcolor: "background.paper",
                color: "text.secondary",
                fontSize: "0.75rem",
                fontWeight: 600,
                lineHeight: 1.5,
            }}
        >
            {category.label}
        </Box>
    ),
    Row: ({children, ref: _ref, ...props}) => (
        <Box {...props} sx={{px: 1, scrollMarginBlock: "6px"}}>
            {children}
        </Box>
    ),
    Emoji: ({emoji, ref: _ref, ...props}) => (
        <Box
            {...props}
            component="button"
            type="button"
            sx={theme => ({
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                p: 0,
                border: 0,
                borderRadius: 1,
                bgcolor: "transparent",
                cursor: "pointer",
                fontSize: "1.3rem",
                transition: theme.transitions.create(["background-color", "transform"], {
                    duration: theme.transitions.duration.shortest,
                }),
                "&[data-active]": {
                    bgcolor: "action.hover",
                    transform: "scale(1.08)",
                },
                "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: -2,
                },
                "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                },
            })}
        >
            {emoji.emoji}
        </Box>
    ),
};

const emojiPickerLocale = (language: string): Locale => {
    const normalized = language.toLowerCase();
    if (normalized.startsWith("de")) return "de";
    if (normalized.startsWith("it")) return "it";
    if (normalized.startsWith("en-gb")) return "en-gb";
    return "en";
};

const ActiveEmoji = () => {
    const {t} = useTranslation();
    const emoji = useActiveEmoji();

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{height: 48, px: 1.5, maxWidth: "var(--frimousse-viewport-width)"}}
        >
            <Typography component="span" sx={{fontSize: "1.25rem", lineHeight: 1}}>
                {emoji?.emoji ?? "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
                {emoji?.label ?? t("Insert emoji")}
            </Typography>
        </Stack>
    );
};

function EmojiPicker({onSelect, autoFocus = true}: EmojiPickerProps) {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down("sm"));
    const locale = emojiPickerLocale(i18n.resolvedLanguage || i18n.language);

    return (
        <Paper
            variant="outlined"
            sx={{
                width: "fit-content",
                maxWidth: "calc(100vw - 32px)",
                overflow: "hidden",
                borderRadius: 2,
                bgcolor: "background.paper",
                "& [frimousse-loading], & [frimousse-empty]": {
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    px: 2,
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    textAlign: "center",
                },
            }}
        >
            <FrimousseEmojiPicker.Root
                columns={compact ? 7 : 9}
                locale={locale}
                onEmojiSelect={onSelect}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "auto minmax(0, 1fr) auto",
                        alignItems: "center",
                        gap: 1,
                        m: 1,
                        px: 1.25,
                        minHeight: 40,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.default",
                        color: "text.secondary",
                        "&:focus-within": {
                            borderColor: "primary.main",
                            boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`,
                        },
                        "& [frimousse-search]": {
                            minWidth: 0,
                            width: "100%",
                            p: 0,
                            border: 0,
                            outline: 0,
                            bgcolor: "transparent",
                            color: "text.primary",
                            font: "inherit",
                            fontSize: "0.875rem",
                            "&::placeholder": {
                                color: "text.secondary",
                                opacity: 1,
                            },
                            "&::-webkit-search-cancel-button": {
                                cursor: "pointer",
                            },
                        },
                    }}
                >
                    <SearchRoundedIcon aria-hidden="true" fontSize="small"/>
                    <FrimousseEmojiPicker.Search
                        autoFocus={autoFocus}
                        aria-label={t("Search emojis")}
                        placeholder={t("Search emojis")}
                    />
                    <Box
                        component={FrimousseEmojiPicker.SkinToneSelector}
                        aria-label={t("Change emoji skin tone")}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 30,
                            height: 30,
                            p: 0,
                            border: 0,
                            borderRadius: 1,
                            bgcolor: "transparent",
                            cursor: "pointer",
                            fontSize: "1.1rem",
                            "&:hover": {bgcolor: "action.hover"},
                            "&:focus-visible": {
                                outline: "2px solid",
                                outlineColor: "primary.main",
                                outlineOffset: 1,
                            },
                        }}
                    />
                </Box>

                <Divider/>

                <FrimousseEmojiPicker.Viewport style={{height: compact ? 284 : 320}}>
                    <FrimousseEmojiPicker.Loading>{t("Loading")}…</FrimousseEmojiPicker.Loading>
                    <FrimousseEmojiPicker.Empty>
                        {({search}) => t('No emoji matches "{{query}}"', {query: search})}
                    </FrimousseEmojiPicker.Empty>
                    <FrimousseEmojiPicker.List components={listComponents}/>
                </FrimousseEmojiPicker.Viewport>

                <Divider/>

                <ActiveEmoji/>
            </FrimousseEmojiPicker.Root>
        </Paper>
    );
}

export default EmojiPicker;
